import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect, useState } from 'react';
import { Formik } from 'formik';

import type { ChangePasswordHandler } from '../types';
import buildChangeUserPasswordRows from './buildChangeUserPasswordRows.helper';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';

const { Box, Button, Table, TextField } = vp;

type ChangeUserPasswordProps = {
  handlers: ChangePasswordHandler;
  defaultValues: vp.UserChangePasswordRequest;
  reviewing: boolean;
};
type FC = FunctionComponent<ChangeUserPasswordProps>;
const ChangeUserPassword: FC = (props: ChangeUserPasswordProps) => {
  const { handlers, defaultValues, reviewing } = props;
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const changePasswordRow = buildChangeUserPasswordRows();
  const [buttonIsDisabled, setButtonIsDisabled] = useState<boolean>(true);
  const { t } = useTranslation('translations', { keyPrefix: 'Users.ChangeUserPassword' });

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={reviewing ? handlers.onChangeUserPassword : handlers.onReview}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        useEffect(() => {
          const hasErrors = Object.entries(errors).length > 0;
          const allEmptyFields = Object.values(values).every((val) => !val);

          setButtonIsDisabled(allEmptyFields || hasErrors);
        }, [errors]);

        const changePasswordTable: vp.TableProps = {
          name: 'change-password-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: changePasswordRow.map(({ label, id, preset }) => ({
            Field: label,
            Input: (
              <TextField
                preset={preset}
                key={id}
                name={id}
                variant="standard"
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] && touched[id] ? errors[id] : null}
                disabled={reviewing}
              />
            ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Table {...changePasswordTable} />
            <Box mt="md" jc="fe">
              <Button
                id="CreateLead-submit"
                type="submit"
                label={reviewing ? t('Review.submitButton') : t('submitButton')}
                isDisabled={buttonIsDisabled || isRoleViewOnly}
              />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      NewPassword: Yup.string().label(t('newPasswordLabel')).required(),
    });
  }
};

export default ChangeUserPassword;
